import { cn } from '@/lib/utils.ts'
import { type JSX, useState } from 'react'

import { Button } from '@/components/ui/button.tsx'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip.tsx'

import { BlockerIcon } from '@/components/BlockerIcon.tsx'
import { BlockerStoryIdMarker } from '@/components/BlockerStoryIdMarker.tsx'
import { MentionInputComponent, getMentionSuggestionsStyle } from '@/components/Mention.tsx'
import { TEMPORARY_ID_PREFIX } from '@/constants/PREFIX.ts'
import { getStory } from '@/requests/apollo/cache.ts'
import { useBlockerMutation } from '@/requests/mutations/useMutateEntity.ts'
import { chainFunctions } from '@/utils/call.ts'
import { clickCheckboxWithId } from '@/utils/dom.ts'
import { getTemporaryId, isEntityUpdate, isTemporaryId } from '@/utils/key.ts'
import { onEnterInclusive } from '@/utils/keyboard.ts'
import { GlobalPubSub } from '@/utils/pubsub.ts'
import { scrollToStory } from '@/utils/scroll.ts'
import { storyIdsFromDescription } from '@/utils/storyIdParser.ts'
import { identifyUsername } from '@/utils/string.ts'

type Props = {
  modes: AvailableMode[]
  blocker: Blocker
  placeholder?: string
}

export const Blocker = ({ modes, blocker, placeholder }: Props) => {
  const [description, setDescription] = useState(blocker.description)
  const [isEditMode, setIsEditMode] = useState(modes[0] === 'edit')
  const [isFocused, setIsFocused] = useState(false)
  const { create, update, remove } = useBlockerMutation(blocker.id)

  const onSubmit = () => {
    if (description.trim().length === 0) {
      return
    }

    if (isEntityUpdate(blocker)) {
      update({ description })
      setIsEditMode(false)
    } else {
      const newBlockerId = getTemporaryId()
      create({ ...blocker, id: newBlockerId, description })
      setDescription('')
      document.getElementById(`blocker-content-input-${TEMPORARY_ID_PREFIX}${blocker.storyId}`)?.focus()
    }
  }

  const onCancel = () => {
    setDescription(blocker.description)
    setIsEditMode(false)
  }

  const BlockerDescription = () => {
    const identifiedDescription = identifyUsername(description)
    const storyIds = storyIdsFromDescription(identifiedDescription)

    const elements: (string | JSX.Element)[] = storyIds.reduce<(string | JSX.Element)[]>(
      (acc, storyId, storyIdIndex) => {
        let replaced = false

        return acc.flatMap((part) => {
          if (typeof part !== 'string') {
            return part
          }

          const splitParts = part.split(`#${storyId}`)
          const newElements: (string | JSX.Element)[] = []

          splitParts.forEach((text, index) => {
            if (text !== '') {
              newElements.push(text)
            }

            if (index < splitParts.length - 1) {
              if (!replaced) {
                const blockerStoryIdMarker = (
                  <BlockerStoryIdMarker
                    storyId={storyId}
                    key={`${blocker.id}-${storyId}-${index}-${storyIdIndex}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      if (!getStory(storyId)) return

                      chainFunctions(
                        () => GlobalPubSub.publish(GlobalPubSub.Signals.SELECT_LABEL, null),
                        () => clickCheckboxWithId('toggle_blockers_filter', 'unchecked'),
                        () => {
                          clickCheckboxWithId('done_checkbox', 'checked')
                          clickCheckboxWithId('relevant_checkbox', 'checked')
                          clickCheckboxWithId('unscheduled_checkbox', 'checked')
                        },
                        () => scrollToStory(storyId, 'smooth'),
                        () => GlobalPubSub.publish(GlobalPubSub.Signals.TOGGLE_STORY_FORM, storyId, true),
                      )
                    }}
                  />
                )
                if (getStory(storyId)) {
                  newElements.push(blockerStoryIdMarker)
                } else {
                  newElements.push(
                    <TooltipProvider key={`${blocker.id}-${storyId}-${index}-${storyIdIndex}`}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>{blockerStoryIdMarker}</div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Accepted</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>,
                  )
                }
                replaced = true
              } else {
                newElements.push(`#${storyId}`)
              }
            }
          })

          return newElements
        })
      },
      [identifiedDescription],
    )

    return (
      // biome-ignore lint/a11y/useKeyWithClickEvents:
      <div
        className={cn(
          'flex-1 min-w-0 font-EN-KR-body2-regular text-contents-primary cursor-pointer flex items-center gap-1 break-all',
          blocker.resolved ? 'text-contents-disabled' : '',
        )}
        onClick={() => {
          if (modes.includes('edit')) {
            setIsEditMode(true)
            setTimeout(() => document.getElementById(`blocker-content-input-${blocker.id}`)?.focus())
          }
        }}
      >
        {elements}
      </div>
    )
  }

  if (isTemporaryId(blocker.id) && !blocker.__typename) {
    return (
      <div className="flex min-h-10 gap-2 items-center py-1 px-2 rounded-xs bg-surface-neutralMedium">
        {isFocused || description.trim().length > 0 ? (
          <BlockerIcon size="sm" isBlocked={true} />
        ) : (
          <img src="/images/entity-add-icon.svg" alt="add blocker icon" />
        )}
        <div className="flex-1 min-w-0 relative">
          <MentionableBlocker
            blockerId={blocker.id}
            description={description}
            placeholder={placeholder ?? 'Blocker reason (e.g,story # or @user)'}
            setDescription={setDescription}
            isFocused={isFocused}
            setIsFocus={setIsFocused}
            onSubmit={onSubmit}
            onCancel={onCancel}
          />
        </div>
        {(isFocused || description.trim().length > 0) && (
          <div className="flex gap-1">
            <Button
              type="button"
              variant="ghost"
              className="flex-none py-1 px-[3px] w-[50px] h-8 rounded-xs font-EN-KR-body2-regular"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="flex-none py-1 px-[3px] w-[43px] h-8 rounded-xs font-EN-KR-body2-bold"
              onClick={onSubmit}
              disabled={!description}
            >
              {isEntityUpdate(blocker) ? 'Save' : 'Add'}
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <>
      {isEditMode ? (
        <div className="flex min-h-10 gap-2 items-center py-1 px-2 rounded-xs bg-surface-neutralMedium">
          <BlockerIcon size="sm" isBlocked={true} />
          <div className="flex-1 min-w-0 relative">
            <MentionableBlocker
              blockerId={blocker.id}
              description={description}
              placeholder={placeholder ?? 'Blocker reason (e.g,story # or @user)'}
              setDescription={setDescription}
              isFocused={isFocused}
              setIsFocus={setIsFocused}
              onSubmit={onSubmit}
              onCancel={onCancel}
            />
          </div>
          <div className="flex gap-1">
            <Button
              type="button"
              variant="ghost"
              className="flex-none py-1 px-[3px] w-[50px] h-8 rounded-xs font-EN-KR-body2-regular"
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="flex-none py-1 px-[3px] w-[43px] h-8 rounded-xs font-EN-KR-body2-bold"
              onClick={onSubmit}
              disabled={!description}
            >
              {isEntityUpdate(blocker) ? 'Save' : 'Add'}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex min-h-10 gap-1 items-center justify-between py-1 px-2 rounded-xs bg-surface-neutralMedium">
          <BlockerIcon size="sm" isBlocked={true} />
          <div className="flex-1 min-w-0">
            <BlockerDescription />
          </div>
          <div className="flex gap-0.5 flex-none">
            {blocker.resolved ? (
              <Button
                type="button"
                variant="icon"
                size="icon"
                className="flex-none"
                onClick={() => update({ resolved: false })}
              >
                <img src="/images/blocker-reopen-icon.svg" alt="reopen blocker" />
              </Button>
            ) : (
              <Button
                type="button"
                variant="icon"
                size="icon"
                className="flex-none"
                onClick={() => update({ resolved: true })}
              >
                <img src="/images/blocker-resolve-icon.svg" alt="resolve blocker" />
              </Button>
            )}

            <Button type="button" size="icon" variant="icon" className="flex-none" onClick={() => remove(blocker.id)}>
              <img src="/images/entity-delete-icon.svg" alt="delete blocker" />
            </Button>
          </div>
        </div>
      )}
    </>
  )
}

type MentionableBlockerProps = {
  blockerId: string
  description: string
  placeholder: string
  setDescription: (description: string) => void
  isFocused: boolean
  setIsFocus: (isFocused: boolean) => void
  onSubmit: () => void
  onCancel: () => void
}

const MentionableBlocker = ({
  blockerId,
  description,
  placeholder,
  setDescription,
  setIsFocus,
  onSubmit,
  onCancel,
}: MentionableBlockerProps) => {
  return (
    <MentionInputComponent
      id={`blocker-content-input-${blockerId}`}
      value={description}
      onChange={(e: { target: { value: string } }) => {
        if (e.target.value === '\n') {
          setDescription('')
          return
        }
        setDescription(e.target.value)
      }}
      style={getMentionSuggestionsStyle({
        suggestions: {
          list: {
            position: 'absolute',
            marginTop: '10px',
            marginLeft: '7px',
            width: 'max-content',
          },
        },
      })}
      placeholder={placeholder}
      classNames={{
        'mentions__highlighter': '!border-none',
        'mentions__control': cn(
          'w-full p-2 flex flex-row items-center rounded-xs',
          'font-EN-KR-body2-regular text-contents-primary z-50',
        ),
        'mentions__input': cn(
          '!font-EN-KR-body2-regular w-full p-2 rounded-xs placeholder:!text-contents-secondary',
          '!ring-0 !ring-offset-0 focus-visible:outline focus-visible:outline-1 focus-visible:outline-border-default-neutral focus-visible:!outline-border-default-focused focus-visible:!bg-surface-backgroundElevated focus-visible:placeholder:!text-contents-placeholder',
          'text-ellipsis overflow-hidden',
        ),
      }}
      allowSuggestionsAboveCursor
      onFocus={() => setIsFocus(true)}
      onBlur={() => setIsFocus(false)}
      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        e.stopPropagation()
        onEnterInclusive(e, onSubmit)
        if (e.key === 'Escape') onCancel()
      }}
      markup="**@__display__**"
    />
  )
}
